import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PrimaryTabNavigation from 'components/ui/PrimaryTabNavigation';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const TYPE_COLORS = {
  game: '#6366F1',
  animation: '#F59E0B',
  video: '#10B981',
  'music-video': '#F43F5E',
  music: '#8B5CF6',
};

const TYPE_LABELS = {
  game: 'Game',
  animation: 'Animation',
  video: 'Video',
  'music-video': 'Music Video',
  music: 'Music',
};

function StatCard({ label, value, icon, color }) {
  return (
    <div className="flex flex-col items-center gap-1 p-3 rounded-xl" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon name={icon} size={16} color={color} />
      </div>
      <p className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{typeof value === 'number' ? value?.toLocaleString() : value}</p>
      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{label}</p>
    </div>
  );
}

function CreationCard({ item }) {
  const typeColor = TYPE_COLORS?.[item?.type] || '#6366F1';
  const typeLabel = TYPE_LABELS?.[item?.type] || 'Creation';
  return (
    <div
      className="rounded-2xl overflow-hidden group transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
      style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
    >
      <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
        {item?.thumbnail ? (
          <Image src={item?.thumbnail} alt={item?.title || 'Creation thumbnail'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: `${typeColor}15` }}>
            <Icon name={item?.type === 'music' ? 'Music' : item?.type === 'game' ? 'Gamepad2' : 'Video'} size={32} color={typeColor} />
          </div>
        )}
        <span className="absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: typeColor, color: 'white', fontFamily: 'var(--font-caption)' }}>
          {typeLabel}
        </span>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold truncate mb-1" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{item?.title || 'Untitled'}</h3>
        <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
          <span style={{ fontFamily: 'var(--font-data)' }}>{item?.views?.toLocaleString?.()} views</span>
          <span style={{ fontFamily: 'var(--font-caption)' }}>{item?.createdAt ? new Date(item?.createdAt)?.toLocaleDateString() : ''}</span>
        </div>
      </div>
    </div>
  );
}

export default function CreatorProfilePage() {
  const navigate = useNavigate();
  const { username } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [creations, setCreations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('creations');

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { data: creatorData, error } = await supabase
        ?.from('creators')
        ?.select('*')
        ?.eq('username', username)
        ?.single();

      if (error || !creatorData) {
        // Fallback: show mock profile for demo
        setProfile({
          id: username,
          user_id: null,
          display_name: username?.charAt(0)?.toUpperCase() + username?.slice(1) || 'Creator',
          username: username,
          bio: 'AI creator building the future of interactive entertainment with LoopNest.',
          avatar_url: null,
          total_creations: 0,
          total_views: 0,
          followers_count: 0,
          created_at: new Date()?.toISOString(),
        });
        setFollowerCount(0);
        setLoading(false);
        return;
      }

      setProfile(creatorData);
      setFollowerCount(creatorData?.followers_count || 0);

      // Check if current user follows this creator
      if (user?.id && creatorData?.id) {
        const { data: followData } = await supabase
          ?.from('creator_followers')
          ?.select('id')
          ?.eq('creator_id', creatorData?.id)
          ?.eq('follower_id', user?.id)
          ?.single();
        setIsFollowing(!!followData);
      }

      // Load creations
      const [assetsRes, musicRes] = await Promise.all([
        supabase?.from('generated_assets')?.select('*')?.eq('user_id', creatorData?.user_id)?.order('created_at', { ascending: false })?.limit(12),
        supabase?.from('generated_music')?.select('*')?.eq('user_id', creatorData?.user_id)?.order('created_at', { ascending: false })?.limit(12),
      ]);

      const assets = (assetsRes?.data || [])?.map((a) => ({
        id: a?.id,
        title: a?.title || 'Untitled',
        type: a?.asset_type || 'video',
        thumbnail: a?.thumbnail_url,
        views: a?.view_count || 0,
        createdAt: a?.created_at,
      }));

      const music = (musicRes?.data || [])?.map((m) => ({
        id: `music_${m?.id}`,
        title: m?.song_title || 'Untitled Track',
        type: 'music',
        thumbnail: null,
        views: 0,
        createdAt: m?.created_at,
      }));

      setCreations([...assets, ...music]);
    } catch (err) {
      console.error('[CreatorProfile] loadProfile error:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [username, user?.id]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleFollow = async () => {
    if (!user?.id) { navigate('/creator-dashboard'); return; }
    if (!profile?.id) return;
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await supabase?.from('creator_followers')?.delete()?.eq('creator_id', profile?.id)?.eq('follower_id', user?.id);
        await supabase?.from('creators')?.update({ followers_count: Math.max(0, followerCount - 1) })?.eq('id', profile?.id);
        setFollowerCount((v) => Math.max(0, v - 1));
        setIsFollowing(false);
      } else {
        await supabase?.from('creator_followers')?.insert({ creator_id: profile?.id, follower_id: user?.id });
        await supabase?.from('creators')?.update({ followers_count: followerCount + 1 })?.eq('id', profile?.id);
        setFollowerCount((v) => v + 1);
        setIsFollowing(true);
      }
    } catch (err) {
      console.error('[CreatorProfile] follow error:', err?.message);
    } finally {
      setFollowLoading(false);
    }
  };

  const avatarColor = '#6366F1';
  const initials = profile?.display_name?.slice(0, 2)?.toUpperCase() || 'CR';

  const TABS = [
    { id: 'creations', label: 'Creations', icon: 'Grid3X3', count: creations?.length },
    { id: 'about', label: 'About', icon: 'User', count: null },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryTabNavigation />
      <main className="main-content">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm mb-6 transition-all hover:opacity-70"
            style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="ArrowLeft" size={16} color="var(--color-muted-foreground)" />
            Back
          </button>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="flex flex-col items-center gap-3">
                <Icon name="Loader" size={32} color="var(--color-primary)" />
                <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>Loading profile...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Profile header */}
              <div
                className="rounded-2xl p-6 md:p-8 mb-6"
                style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)' }}
              >
                {/* Cover gradient */}
                <div
                  className="h-24 rounded-xl mb-4 -mx-2"
                  style={{ background: `linear-gradient(135deg, ${avatarColor}30, ${avatarColor}10, rgba(139,92,246,0.15))` }}
                />
                <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-12 md:-mt-14">
                  {/* Avatar */}
                  <div
                    className="w-20 h-20 md:w-24 md:h-24 rounded-2xl flex items-center justify-center text-white font-bold text-2xl border-4 flex-shrink-0 overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}aa)`, borderColor: 'var(--color-background)', fontFamily: 'var(--font-heading)' }}
                  >
                    {profile?.avatar_url ? (
                      <img src={profile?.avatar_url} alt={profile?.display_name} className="w-full h-full object-cover" />
                    ) : initials}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <h1 className="text-xl md:text-2xl font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{profile?.display_name}</h1>
                        <p className="text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>@{profile?.username}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleFollow}
                          disabled={followLoading}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-60"
                          style={{
                            background: isFollowing ? 'var(--color-muted)' : 'var(--color-primary)',
                            color: isFollowing ? 'var(--color-foreground)' : 'white',
                            border: isFollowing ? '1px solid var(--color-border)' : 'none',
                            fontFamily: 'var(--font-heading)',
                          }}
                        >
                          <Icon name={isFollowing ? 'UserCheck' : 'UserPlus'} size={15} color={isFollowing ? 'var(--color-foreground)' : 'white'} />
                          {followLoading ? 'Loading...' : isFollowing ? 'Following' : 'Follow'}
                        </button>
                        <button
                          onClick={() => navigate('/creator-dashboard')}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:opacity-80"
                          style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
                        >
                          <Icon name="Wand2" size={15} color="var(--color-muted-foreground)" />
                          Create
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Bio */}
                {profile?.bio && (
                  <p className="text-sm mt-4 leading-relaxed" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-body)' }}>
                    {profile?.bio}
                  </p>
                )}
                {/* Join date */}
                <div className="flex items-center gap-1.5 mt-3">
                  <Icon name="Calendar" size={13} color="var(--color-muted-foreground)" />
                  <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                    Joined {profile?.created_at ? new Date(profile?.created_at)?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Recently'}
                  </span>
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <StatCard label="Creations" value={profile?.total_creations || creations?.length} icon="Layers" color="var(--color-primary)" />
                <StatCard label="Followers" value={followerCount} icon="Users" color="#EC4899" />
                <StatCard label="Total Views" value={profile?.total_views || 0} icon="Eye" color="#F59E0B" />
              </div>

              {/* Tabs */}
              <div className="flex gap-1 p-1 rounded-xl mb-6" style={{ background: 'var(--color-muted)' }}>
                {TABS?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all"
                    style={{
                      background: activeTab === tab?.id ? 'var(--color-card)' : 'transparent',
                      color: activeTab === tab?.id ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                      boxShadow: activeTab === tab?.id ? 'var(--shadow-sm)' : 'none',
                      fontFamily: 'var(--font-heading)',
                    }}
                  >
                    <Icon name={tab?.icon} size={15} color={activeTab === tab?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                    {tab?.label}
                    {tab?.count !== null && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: activeTab === tab?.id ? 'rgba(99,102,241,0.1)' : 'transparent', color: activeTab === tab?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)' }}>
                        {tab?.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              {activeTab === 'creations' && (
                <div>
                  {creations?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3" style={{ border: '1px dashed var(--color-border)', borderRadius: 16 }}>
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.1)' }}>
                        <Icon name="Layers" size={26} color="var(--color-primary)" />
                      </div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>No creations yet</p>
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>This creator hasn't published anything yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {creations?.map((item) => <CreationCard key={item?.id} item={item} />)}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'about' && (
                <div className="rounded-2xl p-6" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
                  <h3 className="text-base font-bold mb-3" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>About {profile?.display_name}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-body)' }}>
                    {profile?.bio || 'No bio provided yet.'}
                  </p>
                  <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Total Creations</p>
                        <p className="text-lg font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{profile?.total_creations || creations?.length}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Total Views</p>
                        <p className="text-lg font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{(profile?.total_views || 0)?.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
